"use client";

import { useState, useEffect, useCallback } from "react";
import { Suspense } from "react";
import { PERIOD_OPTIONS, AllowedPeriods, CandleDataPoint, VolumeDataPoint } from "@/utils/utilities";
import { fetchCandles, fetchCandlesByPool } from "@/services/data.service";
import ChartComponent from "@/components/ChartComponent";
import type { UTCTimestamp } from "lightweight-charts";

type FallbackMode = "none" | "all_time" | "no_history";

function getTimeRangeForPeriod(period: AllowedPeriods): { from: number; to: number } {
  const now = Math.floor(Date.now() / 1000);
  const periodSeconds: Record<string, number> = {
    "24h": 86400,
    "7d": 604800,
    "14d": 1209600,
    "30d": 2592000,
    "90d": 7776000,
    "180d": 15552000,
    "360d": 31536000,
  };
  const seconds = periodSeconds[period] ?? 1209600;
  return { from: now - seconds, to: now };
}

function pickResolution(seconds: number): number {
  if (seconds <= 86400) return 300;          // 24h → 5m
  if (seconds <= 604800) return 3600;        // 7d → 1h
  if (seconds <= 1209600) return 7200;       // 14d → 2h
  if (seconds <= 2592000) return 14400;      // 30d → 4h
  if (seconds <= 7776000) return 43200;      // 90d → 12h
  if (seconds <= 15552000) return 86400;     // 180d → 1d
  return 259200;                              // 360d → 3d
}

// Earliest timestamp we ever expect any pool to have data — chosen well before
// any indexed history, used as the "from" when extending to all-time.
const ALL_TIME_FROM = 1_700_000_000;

export function PoolChartClient({
  poolId,
  token0Name,
  token1Name,
  initialPeriod,
  initialCandleData,
  initialVolumeData,
  initialFallbackMode = "none",
}: {
  poolId?: string;
  token0Name: string;
  token1Name: string;
  initialPeriod: AllowedPeriods;
  initialCandleData: CandleDataPoint[];
  initialVolumeData: VolumeDataPoint[];
  initialFallbackMode?: FallbackMode;
}) {
  const [period, setPeriod] = useState<AllowedPeriods>(initialPeriod);
  const [candleData, setCandleData] = useState<CandleDataPoint[]>(initialCandleData);
  const [volumeData, setVolumeData] = useState<VolumeDataPoint[]>(initialVolumeData);
  const [loading, setLoading] = useState(false);
  const [fallbackMode, setFallbackMode] = useState<FallbackMode>(initialFallbackMode);

  const loadCandles = useCallback(async (p: AllowedPeriods) => {
    setLoading(true);
    try {
      const { from, to } = getTimeRangeForPeriod(p);
      let raw = poolId
        ? await fetchCandlesByPool(poolId, from, to, pickResolution(to - from))
        : await fetchCandles(token0Name, token1Name, from, to);

      let mode: FallbackMode = "none";
      if (raw.length === 0 && poolId) {
        // Selected period has no data; try all-time before declaring empty.
        const allTime = await fetchCandlesByPool(poolId, ALL_TIME_FROM, to, 86_400);
        if (allTime.length > 0) {
          raw = allTime;
          mode = "all_time";
        } else {
          mode = "no_history";
        }
      } else if (raw.length === 0) {
        mode = "no_history";
      }

      const candles: CandleDataPoint[] = raw.map((c) => ({
        time: c.time,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
        baseVolume: c.baseVolume,
      }));
      const volumes: VolumeDataPoint[] = raw.map((c) => ({
        time: c.time,
        value: c.baseVolume,
        color: c.close >= c.open ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)",
      }));
      setCandleData(candles);
      setVolumeData(volumes);
      setFallbackMode(mode);
    } catch (err) {
      console.error("Failed to fetch candles:", err);
    } finally {
      setLoading(false);
    }
  }, [poolId, token0Name, token1Name]);

  useEffect(() => {
    if (period !== initialPeriod) {
      void loadCandles(period);
    }
  }, [period, initialPeriod, loadCandles]);

  return (
    <div className="space-y-3">
      {/* Period pills */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Fallback notice — small, left-aligned, only when relevant */}
        {fallbackMode === "all_time" ? (
          <p className="text-[10px] tracking-[0.04em]" style={{ color: "var(--text-secondary)" }}>
            No activity in the selected window. Showing all-time history.
          </p>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-1 ml-auto">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setPeriod(opt.value); }}
              className="text-[10px] tracking-[0.06em] uppercase px-2.5 py-1 rounded transition-all duration-150"
              style={{
                background: period === opt.value ? "var(--accent)" : "transparent",
                color: period === opt.value ? "var(--bg)" : "var(--text-secondary)",
                border: period === opt.value ? "1px solid var(--accent)" : "1px solid transparent",
                fontWeight: period === opt.value ? 600 : 400,
                cursor: "pointer",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart — same style as token detail page */}
      <section
        className="h-[400px] lg:h-[500px] rounded-[var(--r)] overflow-hidden relative"
        style={{ background: "var(--surface)", border: "1px solid var(--border-default)", opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
        aria-label="Price & Volume Chart"
      >
        {candleData.length > 0 ? (
          <Suspense fallback={<div className="absolute inset-0 flex items-center justify-center"><span className="text-sm" style={{ color: "var(--text-secondary)" }}>Loading chart...</span></div>}>
            <ChartComponent candleData={candleData} volumeData={volumeData} />
          </Suspense>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center p-4">
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                {fallbackMode === "no_history" ? "No trade history for this pool yet." : "No chart data available"}
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
